import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  AdminManualDeleteOptOutMutation,
  AdminManualDeleteOptOutMutationVariables,
  DeleteOptOutListMutation,
  DeleteOptOutListMutationVariables,
  SearchOptOutsQuery,
  SearchOptOutsQueryVariables,
} from "~frontend/__generated__/graphql";
import {
  ADMIN_MANUAL_DELETE_OPT_OUT,
  DELETE_OPT_OUT_LIST,
  GET_OPT_OUT_LISTS,
  GET_SUBACCOUNT_OPT_OUT_NUMBER_DETAILS,
  GET_TENANT_OPT_OUT_NUMBER_DETAILS,
  SEARCH_OPT_OUTS,
} from "~frontend/graphql/optOuts";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useDebounce from "~frontend/util/useDebounce";
import useMutation from "~frontend/util/useMutation";

import TooltipButton from "../TooltipButton";
import FormTextField from "../forms/FormTextField";
import { OptOutListDeleteButton, OptOutListDetail } from "./OptOutListDetail";
import OptedOutNumberDetail from "./OptedOutNumberDetail";
import { useOptOutList } from "./useOptOutList";
import { useOptedOutNumberDetails } from "./useOptedOutNumberDetails";

type OptOutSearchValue =
  | { type: "optedOutNumber"; phoneNumber: string }
  | { type: "optOutList"; id: string; name: string }
  | { type: "placeholder"; text: string };

export default function OptOutSearchDialog({
  open,
  onClose,
  initialSearchValue = null,
}: {
  open: boolean;
  onClose: () => void;
  initialSearchValue?: OptOutSearchValue | null;
}): ReactElement {
  const subaccountContext = useSubaccount();

  const [searchValue, setSearchValue] = useState<OptOutSearchValue | null>(
    initialSearchValue
  );

  const [deleting, setDeleting] = useState(false);

  const confirm = useConfirm();

  const handleClose = useCallback(() => {
    setSearchValue(null);
    onClose();
  }, [setSearchValue, onClose]);

  const {
    data: optOutNumberData,
    loading: optOutNumberDataLoading,
    error: optOutNumberError,
    canDelete: canDeleteOptOutNumber,
    cannotDeleteReason: cannotDeleteOptOutNumberReason,
  } = useOptedOutNumberDetails(
    searchValue?.type === "optedOutNumber" ? searchValue.phoneNumber : null
  );

  const [deleteOptOut] = useMutation<
    AdminManualDeleteOptOutMutation,
    AdminManualDeleteOptOutMutationVariables
  >(ADMIN_MANUAL_DELETE_OPT_OUT, {
    variables: {
      input: {
        phoneNumber:
          searchValue?.type === "optedOutNumber" ? searchValue.phoneNumber : "",
        subaccountId: subaccountContext?.subaccount.id,
      },
    },
    onErrorSnackbarMessage: "Error reversing Opt Out",
    refetchQueries: [
      GET_SUBACCOUNT_OPT_OUT_NUMBER_DETAILS,
      GET_TENANT_OPT_OUT_NUMBER_DETAILS,
    ],
    awaitRefetchQueries: true,
  });

  const handleDeleteOptOut = useCallback(async () => {
    if (
      deleting ||
      searchValue?.type !== "optedOutNumber" ||
      !canDeleteOptOutNumber
    ) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Reverse Opt Out",
      description: (
        <span>
          Are you sure you want to opt{" "}
          {prettyPhoneNumber(searchValue.phoneNumber!)} back in?
        </span>
      ),
    });
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    await deleteOptOut!();
    setDeleting(false);
  }, [deleting, searchValue, canDeleteOptOutNumber, confirm, deleteOptOut]);

  const {
    list: listData,
    optOuts,
    loading: listDataLoading,
    error: listDataError,
    requestNextPageOfOptOuts: requestNextListPage,
    optOutsLoading: nextListPageLoading,
  } = useOptOutList(searchValue?.type === "optOutList" ? searchValue.id : null);

  const [deleteList] = useMutation<
    DeleteOptOutListMutation,
    DeleteOptOutListMutationVariables
  >(DELETE_OPT_OUT_LIST, {
    onErrorSnackbarMessage: "Error deleting list",
    refetchQueries: [GET_OPT_OUT_LISTS],
    awaitRefetchQueries: true,
  });

  const handleDeleteList = useCallback(async () => {
    if (deleting || searchValue?.type !== "optOutList" || !listData) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Opt Out list",
      description: `Are you sure you want to delete the "${listData.name}" Opt Out list?`,
    });
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    await deleteList({
      variables: {
        input: {
          listId: searchValue.id,
        },
      },
    });
    setDeleting(false);
  }, [deleting, searchValue, listData, confirm, deleteList]);

  function renderSearchResult() {
    if (!searchValue) {
      return null;
    }

    switch (searchValue.type) {
      case "optedOutNumber":
        return (
          <OptedOutNumberDetail
            phoneNumber={searchValue.phoneNumber}
            optOutDetails={optOutNumberData}
            optOutDetailsLoading={optOutNumberDataLoading}
            optOutDetailsError={optOutNumberError}
          />
        );
      case "optOutList":
        return (
          <OptOutListDetail
            listDetails={listData}
            listDetailsLoading={listDataLoading}
            listDetailsError={listDataError}
            optOuts={optOuts}
            onRequestNextPage={requestNextListPage}
            nextPageLoading={nextListPageLoading}
            onShowNumberDetails={(phoneNumber: string) =>
              setSearchValue({ type: "optedOutNumber", phoneNumber })
            }
          />
        );
    }
  }

  function renderActions() {
    if (!searchValue) {
      return null;
    }

    switch (searchValue.type) {
      case "optedOutNumber":
        if (!optOutNumberData || optOutNumberData.isTextable) {
          return null;
        }
        return (
          <TooltipButton
            disabled={deleting || !canDeleteOptOutNumber}
            variant="outlined"
            color="error"
            tooltipText={
              !canDeleteOptOutNumber ? cannotDeleteOptOutNumberReason : null
            }
            onClick={handleDeleteOptOut}
          >
            {deleting ? "Reversing..." : "Reverse Opt Out"}
          </TooltipButton>
        );
      case "optOutList":
        if (!listData) {
          return null;
        }
        return (
          <OptOutListDeleteButton
            listDetails={listData}
            isDeleting={deleting}
            onClick={handleDeleteList}
          />
        );
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Search</DialogTitle>
      <DialogContent>
        <OptOutSearch value={searchValue} onChange={setSearchValue} />
        {renderSearchResult()}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
          paddingRight: theme.spacing(3),
          paddingLeft: theme.spacing(3),
          paddingBottom: theme.spacing(3),
        }}
      >
        <Box>{renderActions()}</Box>
        <Button
          disabled={deleting}
          variant="contained"
          color="primary"
          onClick={handleClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const MAX_SEARCH_RESULTS = 25;

function OptOutSearch({
  value,
  onChange,
}: {
  value: OptOutSearchValue | null;
  onChange: (value: OptOutSearchValue | null) => void;
}): ReactElement {
  const { subaccount } = useSubaccount() ?? {};

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const { loading: autocompleteOptionsLoading, data: autocompleteOptionsData } =
    useQuery<SearchOptOutsQuery, SearchOptOutsQueryVariables>(SEARCH_OPT_OUTS, {
      variables: {
        input: {
          subaccountId: subaccount?.id ?? null,
          search: debouncedSearch,
          limit: MAX_SEARCH_RESULTS + 1,
        },
      },
      skip: debouncedSearch.length < 3,
      fetchPolicy: "network-only",
    });

  const autocompleteOptions: OptOutSearchValue[] = useMemo(() => {
    const allResults = [
      ...(autocompleteOptionsData?.searchOptOuts.optOutLists.map(
        list =>
          ({
            type: "optOutList",
            id: list.id,
            name: list.name,
          }) as OptOutSearchValue
      ) ?? []),
      ...(autocompleteOptionsData?.searchOptOuts.optedOutNumbers.map(
        phoneNumber =>
          ({
            type: "optedOutNumber",
            phoneNumber,
          }) as OptOutSearchValue
      ) ?? []),
    ];
    if (allResults.length > MAX_SEARCH_RESULTS) {
      return [
        ...allResults.slice(0, MAX_SEARCH_RESULTS),
        {
          type: "placeholder",
          text: `... over ${MAX_SEARCH_RESULTS} results`,
        },
      ];
    } else {
      return allResults;
    }
  }, [autocompleteOptionsData]);

  return (
    <Autocomplete
      noOptionsText={
        search.length < 3
          ? "Enter at least 3 characters to search..."
          : "No matching Opt Outs or lists found"
      }
      isOptionEqualToValue={(option, value) => {
        switch (option.type) {
          case "optedOutNumber":
            return (
              value.type === "optedOutNumber" &&
              value.phoneNumber === option.phoneNumber
            );
          case "optOutList":
            return value.type === "optOutList" && value.id === option.id;
          default:
            return false;
        }
      }}
      getOptionLabel={option => {
        switch (option.type) {
          case "optedOutNumber":
            return prettyPhoneNumber(option.phoneNumber);
          case "optOutList":
            return option.name;
          case "placeholder":
            return option.text;
        }
      }}
      getOptionDisabled={option => option.type === "placeholder"}
      options={autocompleteOptions}
      filterOptions={options => options}
      loading={
        search.length >= 3 &&
        (autocompleteOptionsLoading || debouncedSearch !== search)
      }
      loadingText="Loading..."
      inputValue={search}
      onInputChange={(_event, newValue) => setSearch(newValue)}
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      renderInput={params => (
        <FormTextField
          {...params}
          label="Search for a phone number or list"
          variant="standard"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {autocompleteOptionsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      popupIcon={<Search />}
      sx={{
        marginBottom: theme.spacing(3),
        // by default MUI flips the popup icon when the dropdown is open
        // this makes sense for the default icon (the down arrow) but not
        // for the search icon
        ".MuiAutocomplete-popupIndicatorOpen": { transform: "none" },
      }}
    />
  );
}
